import { FormikValues, useFormikContext } from "formik";
import * as React from "react";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { cn } from "@volca/ui/lib/utils";

type CheckboxProps = React.ComponentProps<typeof Checkbox>;

interface BaseCheckboxWithLabelProps extends CheckboxProps {
  id: string;
  label: string;
  containerClassName?: string;
  error?: string;
  showError?: boolean;
  disabled?: boolean;
}

export const CheckboxWithLabel = ({
  id,
  label,
  checked,
  indeterminate,
  onCheckedChange,
  containerClassName,
  error,
  showError = false,
  disabled = false,
}: BaseCheckboxWithLabelProps) => (
  <div
    className={cn(
      "items-top flex gap-2",
      containerClassName,
      disabled && "opacity-50",
    )}
  >
    <Checkbox
      id={id}
      checked={checked}
      indeterminate={indeterminate}
      onCheckedChange={onCheckedChange}
      className="mt-1"
      disabled={disabled}
    />
    <div className="grid gap-1.5 leading-none">
      <label
        htmlFor={id}
        className="text-sm leading-6 font-normal peer-data-disabled:cursor-not-allowed peer-data-disabled:opacity-70"
      >
        {label}
      </label>
      {showError && error && <p className="text-xs text-danger">{error}</p>}
    </div>
  </div>
);

interface FormikCheckboxWithLabelProps extends CheckboxProps {
  id: string; // HTML id
  name?: string; // Formik field name (defaults to id)
  label: string;
  containerClassName?: string;
}

export const FormikCheckboxWithLabel = ({
  id,
  name,
  label,
  checked,
  onCheckedChange,
  containerClassName,
  disabled = false,
}: FormikCheckboxWithLabelProps) => {
  const { touched, errors } = useFormikContext<FormikValues>();

  const key = name ?? id;
  return (
    <CheckboxWithLabel
      id={id}
      label={label}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      containerClassName={containerClassName}
      error={String((errors as Record<string, unknown>)?.[key] || "")}
      showError={
        !!(
          (touched as Record<string, unknown>)?.[key] &&
          (errors as Record<string, unknown>)?.[key]
        )
      }
    />
  );
};
